import PropTypes from 'prop-types';
import React, { createContext, useState } from 'react';
import { useSelector } from 'react-redux';

import { CLASS_ENTITY_TYPE } from 'constants/logic';
import URLS from 'constants/urls';
import { transformKeysToCamelCase } from 'helpers/core';
import { reportError } from 'helpers/error-logging';
import { httpClient } from 'helpers/http';
import { selectActiveVenueId, selectVenueSmartspotData } from 'selectors/general';

const MINIMUM_SPOTS_PEAK_TIME = 'minimum_spots_peak_time';
const MINIMUM_SPOTS_OFFPEAK_TIME = 'minimum_spots_offpeak_time';

const DEFAULT_VALUE = {
  venueClasses: [],
  minimumSpotsData: {},
  venueLevelDefaults: {
    minimumSpotsPeakTime: null,
    minimumSpotsOffpeakTime: null,
  },
  isLoading: false,
  error: null,
  fetchData: () => {},
};

const ClassLevelMinimumSpotsContext = createContext(DEFAULT_VALUE);

async function fetchClasses(venueId) {
  try {
    const response = await httpClient.get(URLS.venue.classes(venueId), {
      params: { entity_type: CLASS_ENTITY_TYPE.CLASS },
      ignoreInternalError: true,
    });
    return transformKeysToCamelCase(response.data?.data) || [];
  } catch (error) {
    reportError(error);
    throw error;
  }
}

async function fetchClassLevelMinimumSpots(venueId, classIds) {
  try {
    if (!classIds || classIds.length === 0) {
      return {};
    }

    const response = await httpClient.post(
      URLS.smartSpot.classLevelMinimumSpots(venueId),
      {
        class_ids: classIds,
      },
      {
        ignoreInternalError: true,
      }
    );

    const responseData = transformKeysToCamelCase(response.data?.data) || {};
    const spotsByClassId = Object.entries(responseData).reduce((acc, [classId, spotData]) => {
      acc[classId] = {
        minimumSpotsPeakTime: spotData.minimumSpotsPeakTime ?? null,
        minimumSpotsOffpeakTime: spotData.minimumSpotsOffpeakTime ?? null,
      };
      return acc;
    }, {});

    return spotsByClassId;
  } catch (error) {
    reportError(error);
    throw error;
  }
}

async function updateClassLevelMinimumSpots(venueId, minimumSpotsData) {
  try {
    const payload = Object.entries(minimumSpotsData).map(([classId, spotData]) => {
      const updates = [];

      const spotsFieldMap = {
        minimumSpotsPeakTime: MINIMUM_SPOTS_PEAK_TIME,
        minimumSpotsOffpeakTime: MINIMUM_SPOTS_OFFPEAK_TIME,
      };

      Object.entries(spotsFieldMap).forEach(([key, fieldName]) => {
        if (key in spotData) {
          updates.push({
            field_name: fieldName,
            value: spotData[key],
          });
        }
      });

      return {
        class_id: Number(classId),
        updates,
      };
    });

    const response = await httpClient.patch(URLS.smartSpot.classLevelMinimumSpots(venueId), payload, {
      ignoreInternalError: true,
    });
    return response.data;
  } catch (error) {
    reportError(error);
    throw error;
  }
}

export function ClassLevelMinimumSpotsContextProvider({ children }) {
  const venueId = useSelector(selectActiveVenueId);
  const venueSmartspotData = useSelector(selectVenueSmartspotData);

  const [venueClasses, setVenueClasses] = useState([]);
  const [minimumSpotsData, setMinimumSpotsData] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // Get venue-level defaults
  const venueLevelDefaults = {
    minimumSpotsPeakTime: venueSmartspotData?.minimumSpotsPeakTime ?? null,
    minimumSpotsOffpeakTime: venueSmartspotData?.minimumSpotsOffpeakTime ?? null,
  };

  const fetchData = async () => {
    if (!venueId) {
      return;
    }

    setIsLoading(true);
    setError(null);
    try {
      const classes = await fetchClasses(venueId);
      setVenueClasses(classes);
      const classIds = classes.map(classItem => classItem.classId);

      const minimumSpotsDataResponse = await fetchClassLevelMinimumSpots(venueId, classIds);
      setMinimumSpotsData(minimumSpotsDataResponse);
    } catch (err) {
      setError(err);
      setVenueClasses([]);
      setMinimumSpotsData({});
    } finally {
      setIsLoading(false);
    }
  };

  const updateMinimumSpots = async minimumSpotsDataToUpdate => {
    if (!venueId) {
      throw new Error('Venue ID is required');
    }
    return updateClassLevelMinimumSpots(venueId, minimumSpotsDataToUpdate);
  };

  const value = {
    venueClasses,
    minimumSpotsData,
    venueLevelDefaults,
    isLoading,
    error,
    fetchData,
    updateMinimumSpots,
  };

  return <ClassLevelMinimumSpotsContext.Provider value={value}>{children}</ClassLevelMinimumSpotsContext.Provider>;
}

ClassLevelMinimumSpotsContextProvider.propTypes = {
  children: PropTypes.node,
};

export default ClassLevelMinimumSpotsContext;
